// File: frontend/src/components/ui/tokens.js
//
// §13.1 — ONE type scale, ONE radius set, ONE tap-target floor.
//
// WHY THIS FILE EXISTS
//   The rebuilt pages arrived with seven local styling helpers, and five of them
//   independently declared their own `TYPE` and `RADII` objects. The RADII were
//   byte-identical in every one of them; the TYPE scales agreed on four keys out
//   of six and disagreed on the fifth by half a pixel. That is not a design
//   system, it is five dialects that happen to rhyme.
//
//   This file is the scale. The local helpers keep their own names for their own
//   roles — `TYPE.price` reads better than `TYPE.display` inside a package card,
//   and `TYPE.amount` reads better inside a payment drawer — but both now MAP
//   onto these numbers rather than restating them. A change here moves every
//   surface at once, which is the only property that makes a scale a scale.
//
// WHAT IS DELIBERATELY NOT HERE
//   Colour. Every colour in this app comes from theme.js via the palette, and
//   nothing in the design system may hardcode one. The single exemption is the
//   promotion pink, which has no theme token; it stays local to
//   packages/packagesUiShared.js with the reason written beside it.
//
// THE TOKEN HIERARCHY (§13) — one authority per question, and skins beneath it
//
//   1. theme.js — COLOUR and the MUI theme. The only file that may define a
//      colour value. LIGHT / DARK palettes (including `gold`, the money tone).
//   2. this file — SCALE: type sizes, radii, spacing, the tap-target floor.
//   3. Skins, which DERIVE from 1 and 2 and define no colour of their own:
//        · components/dashboard/dashboardTokens.js — the Router Dashboard's
//          glass surfaces and aurora. A presentation of theme colours, kept
//          because the dashboard's depth treatment is genuinely its own.
//        · pages/super/superShared.js useSuperTokens() — the Super console's
//          alias bag (`C.green`, `C.surface`…) so its pages read the same;
//          every value is a theme palette entry.
//   A new page reads the theme (useTheme / sx palette keys) and this scale. It
//   does not start a fourth bag. The raw-hex lint rule (package.json) enforces
//   rule 1; its exemption list is for brand art and legacy files and is meant
//   to SHRINK — remove a file from it when its last hex goes.

/**
 * The type scale. Six roles, and every font size in the rebuilt surfaces is one
 * of them.
 *
 *   display  the ONE big number on a surface — a price, an amount, a KPI value
 *   heading  a card title, a section heading, a dialog title
 *   action   button and control text
 *   body     a table cell, a detail value, prose that carries a fact
 *   meta     secondary prose, a caption that explains
 *   label    a column header, a chip, a metric label
 *   mono     a reference, an id — monospace, so it sets one notch smaller
 *   micro    a footnote; use sparingly, it is at the edge of legibility
 */
export const TYPE_SCALE = {
  display: 22,
  // A dialog title sits between the two: bigger than a card heading because a
  // modal is the only thing on screen, smaller than a display figure because it
  // is a sentence rather than a number. Three surfaces had it hardcoded as 18.
  subheading: 18,
  heading: 16,
  action: 13,
  body: 13,
  meta: 12.5,
  label: 11.5,
  mono: 11.5,
  micro: 10.5,
};

/**
 * The radius set. Three values, addressed by the KIND of thing being rounded so
 * a caller never has to decide whether a menu is "2 or 3".
 *
 * These are MUI spacing multiples (theme.shape.borderRadius units), matching how
 * every existing surface already writes them.
 */
export const RADII = {
  surface: 3,   // cards, dialogs, panels, drawers
  control: 2,   // buttons, inputs, menus, chips-with-corners
  pill: 99,     // status pills, progress bars, filter chips
};

/**
 * Vertical rhythm. Card padding, section gaps and the space between a label and
 * the thing it labels — in MUI spacing units.
 */
export const SPACING = {
  cardPadding: { xs: 2, sm: 2.5 },
  sectionGap: { xs: 2, sm: 2.5 },
  stackGap: 1.25,
  inlineGap: 0.75,
};

/**
 * The tap-target floor (§13.4 "increase critical tap targets").
 *
 * 44 CSS pixels is the WCAG 2.2 AAA target size and the iOS HIG minimum; 24px
 * is the WCAG 2.2 AA floor. MUI's `size="small"` IconButton is 34px and its
 * `size="small"` Button is 30px tall, so anything CRITICAL — a row's action
 * menu, a bulk action, a pager control, a copy-the-reference button — has to be
 * lifted explicitly. Non-critical, densely-packed controls (a filter chip's
 * delete X, an inline clear) stay at the AA floor rather than blowing the
 * layout apart; the rule is about the controls a mistake is expensive on.
 */
export const MIN_TAP = 44;
export const MIN_TAP_DENSE = 32;

/**
 * Lift a control to the tap-target floor WITHOUT changing its icon size or its
 * visual weight. The box grows; the glyph does not.
 *
 *   <IconButton sx={tapTargetSx()}>…</IconButton>
 *   <Button sx={tapTargetSx({ dense: true })}>…</Button>
 */
export function tapTargetSx({ dense = false, size, touch = false } = {}) {
  const px = size || (dense ? MIN_TAP_DENSE : MIN_TAP);
  // `touch`: only where the pointer is a finger. A row of copy/delete icons
  // keeps its desktop density and grows on a phone, which is where a 25px
  // target turns into a mis-tap (§42).
  if (touch) return { "@media (pointer: coarse)": { minWidth: px, minHeight: px } };
  return { minWidth: px, minHeight: px };
}

/**
 * A 44px hit area for an ISOLATED small mark — an info "i", an inline text
 * button — without moving anything: on a touch screen a transparent ::after
 * reaches 44×44 around the mark's centre.
 *
 * Never on a control that sits beside another control. The later sibling's
 * ::after paints over the earlier one, so in an Edit | Delete pair it would
 * steal the edge of Edit and hand the tap to Delete. Those get
 * tapTargetSx({ touch: true }), which grows the boxes apart instead.
 */
export const tapHitAreaSx = {
  position: "relative",
  "@media (pointer: coarse)": {
    "&::after": {
      content: '""', position: "absolute", left: "50%", top: "50%",
      width: `max(100%, ${MIN_TAP}px)`, height: `max(100%, ${MIN_TAP}px)`,
      transform: "translate(-50%, -50%)",
    },
  },
};

/**
 * Visually hidden, still announced. `clip`, not `display:none` — a display:none
 * element is removed from the accessibility tree, which is the opposite of what
 * screen-reader-only text is for.
 *
 * This is the canonical copy. packages/packagesUiShared.js and
 * transactions/transactionsShared.js both re-export it under their existing
 * `SR_ONLY` name, so their public shape is unchanged and there is now one
 * definition instead of three.
 */
export const SR_ONLY = {
  position: "absolute",
  // Strings, not bare numbers: MUI sx maps a width/height of 1 to 100%,
  // which leaves an invisible full-size box extending the scroll container.
  width: "1px",
  height: "1px",
  p: 0,
  m: "-1px",
  overflow: "hidden",
  clip: "rect(0 0 0 0)",
  whiteSpace: "nowrap",
  border: 0,
};
